import { useEffect, useRef, useState } from 'react';
import { addDays, daysBetween } from '@crmsy/shared';
import { todayIso } from '../lib/today.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { useT } from '../i18n/index.js';

export interface DatePickProps {
  title: string;
  current: string | null;
  /** Offer "✕ None" (clears the date). */
  allowNone?: boolean;
  defaultDays?: 3 | 5 | 7;
  onPick: (date: string | null) => void;
  onClose: () => void;
  anchor?: { x: number; y: number } | null;
}

export const QUICK_DAYS = [3, 5, 7] as const;

/** Follow-up date menu (§6.1, §6.2): 3 · 5 · 7 days · Custom… · None. Keys 1 2 3 · c · n. */
export function DatePick({ title, current, allowNone = false, defaultDays = 7, onPick, onClose, anchor }: DatePickProps) {
  const t = useT();
  const today = todayIso();
  const [custom, setCustom] = useState(current ?? addDays(today, defaultDays));
  const [cursor, setCursor] = useState(QUICK_DAYS.indexOf(defaultDays));
  const customRef = useRef<HTMLInputElement>(null);
  const [customOpen, setCustomOpen] = useState(false);

  const options: { label: string; value: string | null; key: string }[] = [
    ...QUICK_DAYS.map((d, i) => ({ label: t('date.inDays', { n: d }), value: addDays(today, d), key: String(i + 1) })),
    { label: t('date.custom'), value: 'custom', key: 'c' },
    ...(allowNone ? [{ label: t('date.none'), value: null, key: 'n' }] : []),
  ];

  const choose = (i: number) => {
    const o = options[i];
    if (!o) return;
    if (o.value === 'custom') {
      setCustomOpen(true);
      return;
    }
    onPick(o.value);
    onClose();
  };

  useEffect(() => {
    if (customOpen) customRef.current?.focus();
  }, [customOpen]);

  useKeys(
    {
      Escape: onClose,
      j: () => setCursor((c) => Math.min(c + 1, options.length - 1)),
      k: () => setCursor((c) => Math.max(c - 1, 0)),
      ArrowDown: () => setCursor((c) => Math.min(c + 1, options.length - 1)),
      ArrowUp: () => setCursor((c) => Math.max(c - 1, 0)),
      Enter: () => choose(cursor),
      '1': () => choose(0),
      '2': () => choose(1),
      '3': () => choose(2),
      c: () => setCustomOpen(true),
      n: () => {
        if (allowNone) {
          onPick(null);
          onClose();
        }
      },
    },
    { priority: PRIORITY.picker },
  );

  const style = anchor ? { left: Math.min(anchor.x, window.innerWidth - 300), top: anchor.y } : { left: '50%', top: 120, transform: 'translateX(-50%)' };

  return (
    <>
      <div className="fixed inset-0 z-30" onMouseDown={onClose} />
      <div role="listbox" aria-label={title} className="fixed z-40 w-72 rounded-xl border border-line bg-paper shadow-xl" style={style}>
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</span>
          {current && <span className="text-[11px] text-muted">{t('date.now', { date: current, n: daysBetween(today, current) })}</span>}
        </div>
        <div className="py-1">
          {options.map((o, i) => (
            <div
              key={o.label}
              role="option"
              aria-selected={i === cursor}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
              className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${i === cursor ? 'bg-accent-soft' : ''}`}
            >
              <kbd>{o.key}</kbd>
              <span className="flex-1">{o.label}</span>
              {o.value && o.value !== 'custom' && <span className="text-[11px] text-muted">{o.value}</span>}
            </div>
          ))}
        </div>
        {customOpen && (
          <form
            className="flex items-center gap-2 border-t border-line px-3 py-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (/^\d{4}-\d{2}-\d{2}$/.test(custom)) {
                onPick(custom);
                onClose();
              }
            }}
          >
            <input
              ref={customRef}
              type="date"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onClose();
                }
              }}
              className="flex-1 rounded-lg border border-line bg-surface px-2 py-1 text-sm outline-none focus:border-accent"
            />
            <button type="submit" className="btn btn-primary">
              {t('date.set')}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
